/**
 * Terminales de la empresa.
 *
 * La tabla `device` existe desde la primera migración —con sus estados, su
 * fecha de revocación y su motivo— y **nunca hubo una pantalla que la
 * enseñara**. El resultado: perder una tablet no tenía respuesta. No había
 * forma de saber cuántas había ni de dar de baja ninguna.
 *
 * Lo que esta pantalla permite, en una frase: quitarle la llave a un aparato
 * sin echar a su dueño ni cambiar contraseñas.
 */

import { useCallback, useEffect, useState } from 'react';
import { canDeviceSignIn, type DeviceStatus } from '@fibwifire/domain/browser';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface Row {
  id: string;
  label: string | null;
  platform: string;
  model: string | null;
  app_version: string | null;
  status: DeviceStatus;
  user_name: string;
  user_email: string;
  approved_at: string | null;
  approved_by_name: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  last_seen_at: string | null;
  last_sync_at: string | null;
  pending_events: number;
  created_at: string;
}

const ESTADOS: Record<DeviceStatus, { label: string; tone: string }> = {
  PENDIENTE: { label: 'Esperando autorización', tone: 'warn' },
  ACTIVE: { label: 'Autorizado', tone: 'ok' },
  READONLY: { label: 'Sólo lectura', tone: 'warn' },
  LOCKED: { label: 'Bloqueado', tone: 'bad' },
  REVOKED: { label: 'Dado de baja', tone: 'bad' },
};

/** «hace 3 h», que es lo que se quiere saber de una tablet. */
function desde(iso: string | null): string {
  if (iso === null) return 'nunca';
  const horas = (Date.now() - Date.parse(iso)) / 3_600_000;
  if (Number.isNaN(horas)) return '—';
  if (horas < 1) return 'hace menos de una hora';
  if (horas < 48) return `hace ${Math.round(horas)} h`;
  return `hace ${Math.round(horas / 24)} días`;
}

export function DevicesScreen(): React.ReactNode {
  const { api, user } = useSession();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});

  const puedeGestionar = hasPermission(user, 'identity.manage_devices');
  const puedeRevocar = hasPermission(user, 'identity.revoke_device');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: Row[] }>('/devices');
      setRows(data.items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los terminales.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(id: string, accion: () => Promise<unknown>, mensaje: string): Promise<void> {
    setBusy(id);
    setError(null);
    try {
      await accion();
      setNotice(mensaje);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido completar la acción.');
    } finally {
      setBusy(null);
    }
  }

  const esperando = rows.filter((r) => r.status === 'PENDIENTE');

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Terminales</h1>
        <span className="screen__count">{rows.length}</span>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && <p className="screen__note">{notice}</p>}

      <p className="screen__note">
        Un terminal nuevo queda esperando hasta que alguien lo autoriza aquí. Así una contraseña
        filtrada no basta para entrar: hace falta además que el aparato esté aprobado. Al dar de
        baja uno, su sesión se cierra en la siguiente renovación aunque nadie toque la tablet.
      </p>

      {esperando.length > 0 && (
        <p className="screen__note">
          <strong>
            {esperando.length} {esperando.length === 1 ? 'terminal espera' : 'terminales esperan'}{' '}
            autorización.
          </strong>{' '}
          Compruebe que reconoce el aparato y a quién pertenece antes de aprobarlo.
        </p>
      )}

      {loading ? (
        <p className="table__loading">Cargando…</p>
      ) : rows.length === 0 ? (
        <p className="screen__note">
          Todavía no se ha registrado ningún terminal. Aparecen aquí solos, la primera vez que
          alguien entra desde la aplicación.
        </p>
      ) : (
        <div className="terminales">
          {rows.map((row) => {
            const estado = ESTADOS[row.status];
            const entra = canDeviceSignIn(row.status);
            return (
              <article className="terminal" key={row.id}>
                <header className="terminal__head">
                  <h2 className="terminal__nombre">
                    {row.label ?? row.model ?? 'Terminal sin nombre'}
                  </h2>
                  <span className={`pill pill--${estado.tone}`}>{estado.label}</span>
                </header>

                <dl className="terminal__datos">
                  <div>
                    <dt>Usuario</dt>
                    <dd>
                      {row.user_name} <span className="terminal__tenue">{row.user_email}</span>
                    </dd>
                  </div>
                  <div>
                    <dt>Aparato</dt>
                    <dd>
                      {row.platform}
                      {row.app_version !== null ? ` · versión ${row.app_version}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Visto</dt>
                    <dd>{desde(row.last_seen_at)}</dd>
                  </div>
                  <div>
                    <dt>Sincronizado</dt>
                    <dd>
                      {desde(row.last_sync_at)}
                      {row.pending_events > 0 ? ` · ${row.pending_events} sin enviar` : ''}
                    </dd>
                  </div>
                  {row.approved_at !== null && (
                    <div>
                      <dt>Autorizado</dt>
                      <dd>
                        {row.approved_at.slice(0, 10)}
                        {row.approved_by_name !== null ? ` por ${row.approved_by_name}` : ''}
                      </dd>
                    </div>
                  )}
                  {row.revoked_at !== null && (
                    <div>
                      <dt>Baja</dt>
                      <dd>
                        {row.revoked_at.slice(0, 10)}
                        {row.revoke_reason !== null ? ` · ${row.revoke_reason}` : ''}
                      </dd>
                    </div>
                  )}
                </dl>

                {/*
                  Los datos sin enviar se avisan ANTES de dar de baja: la baja
                  no los borra, pero el técnico no podrá enviarlos desde ese
                  aparato, y conviene saberlo antes y no después.
                */}
                {row.pending_events > 0 && row.status !== 'REVOKED' && (
                  <p className="terminal__aviso">
                    Este terminal tiene {row.pending_events} cambios sin enviar. Si lo da de baja,
                    no se pierden, pero tendrán que enviarse desde otro sitio.
                  </p>
                )}

                {!entra.allowed && row.status !== 'REVOKED' && (
                  <p className="terminal__aviso">{entra.reason}</p>
                )}

                <footer className="terminal__acciones">
                  {row.status === 'PENDIENTE' && puedeGestionar && (
                    <>
                      <input
                        className="terminal__campo"
                        placeholder="Nombre, p. ej. «Furgón 2»"
                        value={labels[row.id] ?? ''}
                        onChange={(e) => setLabels({ ...labels, [row.id]: e.target.value })}
                        aria-label="Nombre del terminal"
                      />
                      <button
                        className="button button--primary button--small"
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(
                            row.id,
                            () =>
                              api.post(`/devices/${row.id}/approve`, {
                                ...((labels[row.id] ?? '').trim() === ''
                                  ? {}
                                  : { label: (labels[row.id] ?? '').trim() }),
                              }),
                            'Terminal autorizado.',
                          )
                        }
                      >
                        {busy === row.id ? 'Autorizando…' : 'Autorizar'}
                      </button>
                    </>
                  )}

                  {row.status !== 'REVOKED' && puedeRevocar && (
                    <button
                      className="button button--danger button--small"
                      type="button"
                      disabled={busy !== null}
                      onClick={() => {
                        // El motivo se pide siempre: dentro de un año,
                        // «dado de baja» a secas no distingue una tablet
                        // robada de una que se cambió por otra nueva.
                        const motivo = window.prompt(
                          '¿Por qué se da de baja este terminal? (robo, avería, cambio de aparato…)',
                        );
                        if (motivo === null || motivo.trim() === '') return;
                        void run(
                          row.id,
                          () => api.post(`/devices/${row.id}/revoke`, { reason: motivo.trim() }),
                          'Terminal dado de baja. Su sesión queda cerrada.',
                        );
                      }}
                    >
                      Dar de baja
                    </button>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
